import RightArrow from "assets/images/v2/right-arrow.svg";
import tasksChecklistAnimation from "assets/images/v2/tasks-checklist.gif";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import "./Tasks.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  getSelectedTaskId,
  setSelectedTaskId,
  getTaskFilter,
  setTaskFilter,
  TaskFilterType
} from "features/authSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PendingItem, NeedStatus, Document, NeedType } from "../../dashboardService";
import FileUploadePanel from "components/v2/file-upload-panel/FileUploadPanel";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useNeedsRefetchUntil } from "hooks/useNeedsRefetchUntil";
import useTabletChecker from "hooks/useTabletChecker";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import { TASK_CARD_TITLE, TASK_CARD_HEADING_TEXT } from "utilities/Constant";
import Pill from "components/v2/pill/pill";
import { isNonEmptyString } from "utilities/string";
import ESignPanel from "pages/dashboard/home/v2/eSign-panel/ESignPanel";
import Verification from "pages/dashboard/v2/verification-post-app/verification/Verification";
import { VerificationType } from "pages/online-verification/verificationService";
import { getTaskStatus, getTaskStatusForCount } from "utilities/needsHelper";

const NEEDS_ESIGN_REFETCH_DELAY_MS = 3000;
const MAX_NEED_STATUS_REFETCH_ATTEMPTS = 5;

const isNeedStillInTodoTab = (item: PendingItem | undefined): boolean =>
  !!item && getTaskStatus(item) === TaskFilterType.ToDo;

interface ActiveTask {
  needId: string;
  title: string;
  subTitle?: string;
  assignedTo?: string;
  note?: string;
  documents?: Document[];
  needType?: NeedType;
  status?: string;
  needStatus?: NeedStatus;
  lat?: string;
  packageId?: string;
  isEnabled?: boolean;
}

interface TaskProps {
  title: string;
  subTitle: string;
  isSelected?: boolean;
  isComments?: boolean;
  handleClick?: () => void;
}

const Task = (props: TaskProps) => {
  const { title, subTitle, handleClick, isSelected, isComments } = props;
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  return (
    <button className={`task-item ${(isSelected && (isDesktop || isTablet)) ? "selected" : ""}`} onClick={handleClick}>
      <div className="task-item__text">
        <div className="task-item__title paragraph-medium-medium">{title}</div>
        <div className="task-item__pills-container">
          {isNonEmptyString(subTitle) && (
            <div className="task-item__subtitle">
              <Pill text={subTitle} variant="assigned" />
            </div>
          )}
          {isComments && (
            <div className="task-item__comments">
              <Pill text="New Comments" variant="comments" />
            </div>
          )}
        </div>
      </div>
      {!isDesktop && <img src={RightArrow} alt="Right arrow" />}
    </button>
  );
};

export interface TasksPageProps extends LoadingPageProps {
  isNeedsListLoading: boolean;
  isNeedsListFetching: boolean;
  needsList: PendingItem[];
  refetchNeedsList?: () => Promise<{ data?: PendingItem[] } | undefined>;
}

const Tasks: React.FC<TasksPageProps> = ({
  onLoad,
  isNeedsListLoading,
  isNeedsListFetching,
  needsList = [],
  refetchNeedsList,
}: TasksPageProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [showUpload, setShowUpload] = useState(false);
  const [isMobileSubmitSuccess, setIsMobileSubmitSuccess] = useState(false);
  const [showVerificationDrawer, setShowVerificationDrawer] = useState(true);
  const [taskFilters, setTaskFilters] = useState<{ label: string; value: TaskFilterType; badge?: string; }[]>([
    { label: "To Do", value: TaskFilterType.ToDo },
    { label: "Under Review", value: TaskFilterType.UnderReview },
    { label: "Complete", value: TaskFilterType.Complete },
  ]);

  const [taskVOASimulation, setTaskVOASimulation] = useState<Partial<ActiveTask>>({});

  const selectedTaskIdFromRedux = useSelector(getSelectedTaskId);
  const taskFilter = useSelector(getTaskFilter);
  const dispatch = useDispatch();

  const scheduleStandardNeedsRefetchAfterDelay = useCallback(() => {
    setTimeout(() => {
      void refetchNeedsList?.();
    }, NEEDS_ESIGN_REFETCH_DELAY_MS);
  }, [refetchNeedsList]);

  const safeRefetchNeedsList = useCallback(async () => {
    if (!refetchNeedsList) {
      return undefined;
    }
    return refetchNeedsList();
  }, [refetchNeedsList]);

  const shouldStopNeedsPolling = useCallback((list: PendingItem[], submittedNeedId: string) => {
    const item = list.find((n) => n.id === submittedNeedId);
    return !isNeedStillInTodoTab(item);
  }, []);

  const {
    isPolling: isNeedsSubmitPolling,
    isExpectedStatusNotReceived: isNeedsSubmitExpectedStatusNotReceived,
    startPolling: startNeedsSubmitPolling,
    clearExpectedStatusError: clearNeedsSubmitExpectedStatusError,
  } = useNeedsRefetchUntil({
    refetch: safeRefetchNeedsList,
    isFetching: isNeedsListFetching,
    maxAttempts: MAX_NEED_STATUS_REFETCH_ATTEMPTS,
    shouldStop: shouldStopNeedsPolling,
  });

  const showTaskListLoader = isNeedsListLoading || isNeedsSubmitPolling;

  const getHeader = () => {
    const titleClassName = "heading-medium";
    const title = <h5 className={titleClassName}>{TASK_CARD_TITLE}</h5>;
    const heading = <span className="label-large-regular">{TASK_CARD_HEADING_TEXT}</span>;

    return (
      <div className="tasks__heading">
        {title}
        <section className="tasks__subhead">{heading}</section>
      </div>
    );
  };

  const handleTaskClick = (item: PendingItem) => {
    clearNeedsSubmitExpectedStatusError();
    dispatch(setSelectedTaskId(item.id));

    setActiveTaskId(item.id);

    if (!isDesktop) {
      setShowUpload(true);
    }

    if (item.needType === NeedType.Voa) {
      setShowVerificationDrawer(true);
    }
  };

  useEffect(() => {
    setShowUpload(isDesktop);
  }, [isDesktop]);

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const filteredNeedsList = useMemo(() => {
    return needsList.filter(item => taskFilter === getTaskStatus(item));
  }, [needsList, taskFilter]);

  const selectedTask: ActiveTask | null = useMemo(() => {
    if (!activeTaskId) return null;

    const task = needsList.find(item => item.id === activeTaskId);
    if (!task) return null;

    return {
      needId: task.id,
      title: task.title,
      assignedTo: task.assignedTo,
      note: task.note,
      status: task.status,
      needType: task.needType,
      documents: task.documents,
      needStatus: task.needStatus,
      lat: task.lat,
      packageId: task.packageId,
      isEnabled: task.isEnabled,
      ...taskVOASimulation
    };
  }, [activeTaskId, needsList, taskVOASimulation]);

  const handleDocumentUploadSubmitSuccess = useCallback(() => {
    if (selectedTask?.needId) {
      startNeedsSubmitPolling(selectedTask?.needId);
      // Keep panel open on mobile until user clicks "Back to Tasks"
      if (!isDesktop && !isTablet) {
        setIsMobileSubmitSuccess(true);
      }
    }
  }, [startNeedsSubmitPolling, selectedTask?.needId, isDesktop, isTablet]);

  const handleSetShowUpload = useCallback((show: boolean) => {
    setShowUpload(show);
    // Reset mobile submit success state when user navigates away
    if (!show) {
      setIsMobileSubmitSuccess(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedTaskIdFromRedux) return;

    setActiveTaskId(selectedTaskIdFromRedux);

    if (!isDesktop) {
      setShowUpload(true);
    }

    dispatch(setSelectedTaskId(undefined));

  }, [selectedTaskIdFromRedux, isDesktop, dispatch]);

  useEffect(() => {
    // On mobile, keep showing the submitted task panel until user clicks "Back to Tasks"
    if (isMobileSubmitSuccess && !isDesktop && !isTablet) {
      return;
    }

    if (!filteredNeedsList.length) {
      setActiveTaskId(null);
      return;
    }

    const exists = filteredNeedsList.some(
      task => task.id === activeTaskId
    );

    if (!exists) {
      setActiveTaskId(filteredNeedsList[0].id);
    }

  }, [filteredNeedsList, activeTaskId, isMobileSubmitSuccess, isDesktop, isTablet]);

  useEffect(() => {
    setTaskVOASimulation({});
  }, [activeTaskId]);

  useEffect(() => {
    const updatedFilters = taskFilters.map(filter => {
      const count = needsList.filter(
        item => getTaskStatusForCount(item) === filter.value
      ).length;

      return {
        ...filter,
        badge:
          filter.value === TaskFilterType.ToDo && count > 0
            ? count.toString()
            : ""
      };
    });

    setTaskFilters(updatedFilters);

  }, [needsList]);

  useEffect(() => {
    onLoad?.();
  }, []);

  const handleClosedVerificationDrawer = () => {
    if (!isDesktop) setShowUpload(false);
    setShowVerificationDrawer(false);
  }

  // To simulate VOA completion, to be removed later
  const handleVerificationSuccess = () => {
    if (selectedTask) {
      setTaskVOASimulation({ ...selectedTask, status: 'completed' });
    }
  };

  return (
    <main className="tasks-main">
      {(isDesktop || !showUpload || isTablet) && (
        <section className={`tasks ${showUpload ? "hidden-mobile" : ""}`}>
          {getHeader()}
          <section className="tasks__current-complete">
            <TogglePills options={taskFilters} value={taskFilter} onChange={(value) => dispatch(setTaskFilter(value))} />
          </section>
          <section className="tasks__task-items">
          {showTaskListLoader ? (
              <div className="tasks__spinner-container">
                 <Spinner isApi />
              </div>
            ) : isNeedsSubmitExpectedStatusNotReceived ? (
              <div className="tasks__error-state paragraph-medium-medium" role="status" aria-live="polite">
                Unable to retrieve the latest status. Please refresh the page to load the latest information.
              </div>
            ): (
              <>
                {filteredNeedsList.map((item, idx) => {
                  const isSelected = selectedTask?.needId === item.id;
                  return (
                    <Task
                      key={idx}
                      title={item.title}
                      subTitle={item.assignedTo ?? ""}
                      isSelected={isSelected}
                      isComments={item.status === "needsAttention" || !!item.note?.trim()}
                      handleClick={() => {
                        handleTaskClick(item);
                      }}
                    />
                  );
                })}
                {isDesktop && filteredNeedsList.length === 0 && (
                  <div className="tasks__no-selected-tasks paragraph-medium-medium">
                    {taskFilter === TaskFilterType.Complete ? "No completed tasks yet" : "No current tasks"}
                  </div>
                )}
              </>
            )}
          </section>
        </section>
      )}
      <section className="tasks__selected">
        {filteredNeedsList.length === 0 && (
          <section className="tasks__no-tasks">
            <img src={tasksChecklistAnimation} alt="todo list being checked off with a pencil" />
            <span className="paragraph-medium">
              {taskFilter === TaskFilterType.ToDo
                ? "When you have tasks you will be able to view more details here."
                : taskFilter === TaskFilterType.Complete
                  ? "Your completed tasks will show up here"
                  : ""}
            </span>
          </section>
        )}
        {(isDesktop || showUpload) && filteredNeedsList.length > 0 && (
          selectedTask?.needType === NeedType.ESign ? (
            <ESignPanel
              status={selectedTask.status || ""}
              loanAccessToken={selectedTask.lat}
              packageId={selectedTask.packageId}
              setShowUpload={handleSetShowUpload}
              isEnabled={selectedTask.isEnabled}
              onESignDrawerClosed={scheduleStandardNeedsRefetchAfterDelay}
            />
          ) : (
            selectedTask?.needType !== NeedType.Voa && (
              <FileUploadePanel
                needId={selectedTask?.needId || ""}
                title={selectedTask?.title || ""}
                assignedTo={selectedTask?.assignedTo}
                setShowUpload={handleSetShowUpload}
                note={selectedTask?.note}
                documents={selectedTask?.documents}
                onSubmitSuccess={handleDocumentUploadSubmitSuccess}
              />
            )
          ))}
        {selectedTask?.needType === NeedType.Voa && showVerificationDrawer && (
          <div className="verification-container">
            <Verification
              verificationType={VerificationType.VOA}
              closeDrawerForMobile={handleClosedVerificationDrawer}
              status={selectedTask?.status || ""}
              handlesuccessfullVerification={handleVerificationSuccess}
            />
          </div>
        )}
      </section>
    </main>
  );
};

export default Tasks;